import {callAPI} from 'services/api';
import config from 'config';
import {Design} from 'services/entities/designs';
import {CancelledError, NotFoundError} from 'util/errors';

const MAX_DESIGN_SIZE = 5000;
const API_URL = 'https://sdk.canva.com/designbutton/v2/api.js';

let _apiPromise: Promise<any>|null = null;

async function initAPI(): Promise<any> {
  if(_apiPromise) {
    return _apiPromise;
  }
  console.log('Initializing Canva');
  _apiPromise = new Promise<any>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = API_URL;
    script.onload = function () {
      const win = window as any;
      const canvaButton = win.Canva && win.Canva.DesignButton;
      if (canvaButton) {
        canvaButton.initialize({
          apiKey: config.canva.apiKey,
        }).then(
          (api:any) => {
            console.log('Canva initialized');
            resolve(api);
          },
          (err:any) => {
            console.warn('Canva failed to initialize', err);
            reject(err);
          });
      } else {
        console.warn('Canva failed to initialize - API not loaded');
        reject(new Error("Canva API not loaded"));
      }
    };
    document.body.appendChild(script);
  });
  return _apiPromise;
}

export async function editScreenGroup(design: Design, screenGroupCode: string): Promise<void> {
  const designScreenGroup = design.screenGroups.find(sg => sg.screenGroup.code === screenGroupCode);
  if(!designScreenGroup) {
    throw new NotFoundError("Design screen group not found");
  }
  const designID = designScreenGroup.sourceData?.canva_design_id;
  let width = designScreenGroup.screenGroup.resolution.width;
  let height = designScreenGroup.screenGroup.resolution.height;
  if(width > MAX_DESIGN_SIZE || height >= MAX_DESIGN_SIZE) {
    const scale = Math.min(MAX_DESIGN_SIZE / width, MAX_DESIGN_SIZE / height);
    width *= scale;
    height *= scale;
  }
  const api = await initAPI();
  if(designID) {
    return new Promise<void>((resolve, reject) => {
      api.editDesign({
        design: {
          id: designID
        },
        editor: { fileType: 'png' },
        onDesignPublish: (options: { exportUrl: string, designId: string }) => {
          uploadDesign(design.code, screenGroupCode, options.exportUrl, options.designId).then(resolve, reject);
        },
        onDesignClose: () => { reject(new CancelledError()); },
      });
    });
  } else {
    let designCreated = false;
    return new Promise<void>((resolve, reject) => {
      api.createDesign({
        design: {
          type: 'Presentation',
          dimensions: {
            units: 'px',
            width: Math.round(width),
            height: Math.round(height),
          }
        },
        editor: { fileType: 'png' },
        onDesignOpen: (options: {designId: string}) => {
          designCreated = true;
          setDesignID(design.code, screenGroupCode, options.designId);
        },
        onDesignPublish: (options: { exportUrl: string, designId: string }) => {
          uploadDesign(design.code, screenGroupCode, options.exportUrl, options.designId).then(resolve, reject);
        },
        onDesignClose: () => {
          if(designCreated) {
            resolve(); // design was created, but not published - treat as success
          } else {
            reject(new CancelledError());
          } },
      });
    });
  }
}

async function setDesignID(designCode: string, screenGroupCode: string, canvaDesignID: string): Promise<any> {
  await callAPI(`/canva/designs/${designCode}/screen-groups/${screenGroupCode}/`, 'PUT', {
    design_id: canvaDesignID
  }, true);
}

async function uploadDesign(designCode: string, screenGroupCode: string, url: string, canvaDesignID: string): Promise<any> {
  await callAPI(`/canva/designs/${designCode}/screen-groups/${screenGroupCode}/import/`, 'PUT', {
    url: url,
    design_id: canvaDesignID
  }, true);
}